import { useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle } from 'lucide-react';
import { useFlexEUpload } from '@/hooks/useFlexEUpload';
import { useToast } from '@/hooks/use-toast';
import FlexEDataPreview from './FlexEDataPreview';

interface FlexEUploadCardProps {
  onUploadSuccess: (uploadId: string) => void;
}

export default function FlexEUploadCard({ onUploadSuccess }: FlexEUploadCardProps) {
  const { uploadFile, uploading, uploadProgress } = useFlexEUpload();
  const { toast } = useToast();
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = (file: File): string | null => {
    // Check file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return 'Bestand is te groot (maximaal 10MB)';
    }
    
    // Check file type
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    if (!fileExtension || !validExtensions.includes(fileExtension)) {
      return 'Ongeldig bestandstype. Upload een Excel (.xlsx, .xls) of CSV bestand.';
    }
    
    return null;
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      await handleUpload(files[0]);
    }
  }, []);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      await handleUpload(files[0]);
    }
  };

  const handleUpload = async (file: File) => {
    setError(null);
    
    // Client-side validation
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      toast({
        title: 'Validatie mislukt',
        description: validationError,
        variant: 'destructive'
      });
      return;
    }
    
    const result = await uploadFile(file);
    if (result) {
      setUploadResult(result);
      onUploadSuccess(result.upload_id);
      setError(null);
    } else {
      setError('Upload mislukt. Controleer het bestand en probeer opnieuw.');
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-2xl">1️⃣</span>
        <h2 className="text-xl font-semibold">Upload Kwartierwaarden</h2>
      </div>
      
      <p className="text-sm text-muted-foreground mb-4">
        Upload de Excel-export van de netbeheerder met kolommen: 
        <strong> Tijdstip</strong> en <strong>Piekbelasting (kW)</strong>
      </p>

      {!uploadResult ? (
        <>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-primary bg-primary/5' : 'border-border'
            } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="mb-2">Sleep bestand hierheen of klik om te selecteren</p>
            <p className="text-xs text-muted-foreground mb-4">
              Maximaal 10MB, formaten: XLSX, XLS, CSV
            </p>
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              disabled={uploading}
            />
            <Button
              onClick={() => document.getElementById('file-upload')?.click()}
              disabled={uploading}
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Selecteer bestand
            </Button>
          </div>

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Upload mislukt</AlertTitle>
              <AlertDescription>
                {error}
                <Button 
                  variant="link" 
                  size="sm" 
                  className="mt-2 p-0 h-auto text-destructive underline"
                  onClick={() => setError(null)}
                >
                  Probeer opnieuw
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div>
              <p className="font-medium text-green-900 dark:text-green-100">
                Upload geslaagd: {uploadResult.filename}
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                {uploadResult.rows.toLocaleString()} rijen • 
                {' '}{uploadResult.date_start} tot {uploadResult.date_end}
              </p>
            </div>
          </div>

          <FlexEDataPreview data={uploadResult.preview} />

          <Button
            variant="outline"
            onClick={() => {
              setUploadResult(null);
              onUploadSuccess('');
            }}
          >
            Ander bestand uploaden
          </Button>
        </div>
      )}

      {uploading && (
        <div className="mt-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span>Bestand verwerken...</span>
            <span>{uploadProgress}%</span>
          </div>
          <Progress value={uploadProgress} />
          <p className="text-xs text-muted-foreground text-center">
            Dit kan enkele seconden duren voor grote bestanden
          </p>
        </div>
      )}
    </Card>
  );
}
